import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBRow,
  MDBCol,
  MDBInput,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/**
 * FuncionFormModal — crear o editar una función (Representacion).
 *
 * Props:
 *  - show: boolean
 *  - funcion: objeto FuncionListItemDTO (null si es nueva función)
 *  - proyectos: array [{id, title}] para el selector de obra
 *  - onClose: () => void
 *  - onSaved: (funcion) => void — callback con el DTO guardado
 */
class FuncionFormModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState(props.funcion, props.proyectos);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState(this.initialState(this.props.funcion, this.props.proyectos));
      this.loadVenues();
    }
  }

  loadVenues = () => {
    doFetchJson("TEAN", "getVenues")
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          this.setState({ venues: json.DATA || [] });
        }
      })
      .catch(() => {});
  };

  initialState = (funcion, proyectos) => {
    if (funcion) {
      return {
        isEdit: true,
        name: funcion.name || "",
        idProyecto: funcion.idProyecto ? String(funcion.idProyecto) : "",
        address: funcion.address || "",
        description: funcion.description || "",
        enlaceUrl: funcion.enlaceUrl || "",
        status: funcion.status || "DRAFT",
        idVenue: funcion.idVenue ? String(funcion.idVenue) : "",
        venues: [],
        saving: false,
      };
    }
    return {
      isEdit: false,
      name: "",
      idProyecto:
        proyectos && proyectos.length === 1 ? String(proyectos[0].id) : "",
      address: "",
      description: "",
      enlaceUrl: "",
      status: "DRAFT",
      idVenue: "",
      venues: [],
      saving: false,
    };
  };

  handleClose = () => {
    if (this.state.saving) return;
    if (this.props.onClose) this.props.onClose();
  };

  handleChange = (field) => (e) => {
    this.setState({ [field]: e.target.value });
  };

  validate = () => {
    const { name, idProyecto, isEdit } = this.state;
    if (!name.trim()) {
      toast.show({ message: "El nombre de la función es obligatorio", type: "warning" });
      return false;
    }
    if (!isEdit && !idProyecto) {
      toast.show({ message: "Debes seleccionar una obra", type: "warning" });
      return false;
    }
    return true;
  };

  handleSave = () => {
    if (!this.validate()) return;
    const { funcion } = this.props;
    const { isEdit, name, idProyecto, address, description, enlaceUrl, status, idVenue } = this.state;

    const body = {
      name: name.trim(),
      address: address.trim() || null,
      description: description.trim() || null,
      enlaceUrl: enlaceUrl.trim() || null,
      status: status || "DRAFT",
      idVenue: idVenue ? parseInt(idVenue, 10) : null,
    };
    if (isEdit && funcion) {
      body.idRepresentacion = funcion.idRepresentacion;
    } else {
      body.idProyecto = parseInt(idProyecto, 10);
    }

    this.setState({ saving: true });
    doFetchJson("TEAN", "upsertFuncion", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({
            message: isEdit ? "Función actualizada" : "Función creada",
            type: "success",
          });
          if (this.props.onSaved) this.props.onSaved(json.DATA);
          if (this.props.onClose) this.props.onClose();
        } else {
          toast.show({
            message: (json && json.MESSAGE) || "Error al guardar la función",
            type: "danger",
          });
        }
      })
      .catch(() => {
        toast.show({ message: "Error de conexión al guardar la función", type: "danger" });
      })
      .finally(() => this.setState({ saving: false }));
  };

  render() {
    const { show, proyectos } = this.props;
    const { isEdit, name, idProyecto, address, description, enlaceUrl, status, saving, idVenue, venues } = this.state;

    return (
      <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="theater-masks" className="me-2" />
                {isEdit ? "Editar función" : "Nueva función"}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>

            <MDBModalBody>
              <MDBRow className="g-3">
                {/* Obra — solo al crear */}
                {!isEdit && (
                  <MDBCol size="12">
                    <label className="form-label small text-muted mb-1">
                      Obra <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={idProyecto}
                      onChange={this.handleChange("idProyecto")}
                    >
                      <option value="">-- Selecciona una obra --</option>
                      {(proyectos || []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                  </MDBCol>
                )}

                {/* Nombre */}
                <MDBCol size="12">
                  <MDBInput
                    label="Nombre de la función *"
                    value={name}
                    onChange={this.handleChange("name")}
                    disabled={saving}
                  />
                </MDBCol>

                {/* Estado */}
                <MDBCol size="12" md="6">
                  <label className="form-label small text-muted mb-1">Estado</label>
                  <select
                    className="form-select"
                    value={status}
                    onChange={this.handleChange("status")}
                    disabled={saving}
                  >
                    <option value="DRAFT">Borrador</option>
                    <option value="PUBLISHED">Publicada</option>
                    <option value="ON_SALE">En venta</option>
                    <option value="CLOSED">Cerrada</option>
                    <option value="CANCELLED">Cancelada</option>
                  </select>
                </MDBCol>

                {/* Dirección */}
                <MDBCol size="12">
                  <MDBInput
                    label="Dirección / lugar"
                    value={address}
                    onChange={this.handleChange("address")}
                    disabled={saving}
                  />
                </MDBCol>

                {/* Enlace */}
                <MDBCol size="12">
                  <MDBInput
                    label="Enlace al lugar (URL)"
                    type="url"
                    value={enlaceUrl}
                    onChange={this.handleChange("enlaceUrl")}
                    disabled={saving}
                  />
                </MDBCol>

                {/* Sala (opcional) */}
                <MDBCol size="12">
                  <label className="form-label small text-muted mb-1">
                    <MDBIcon fas icon="building" className="me-1" />
                    Sala con butacas (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={idVenue}
                    onChange={this.handleChange("idVenue")}
                    disabled={saving}
                  >
                    <option value="">Sin sala asignada</option>
                    {venues.map((v) => (
                      <option key={v.idVenue} value={v.idVenue}>
                        {v.name}{v.address ? ` — ${v.address}` : ""}
                      </option>
                    ))}
                  </select>
                  {idVenue && (
                    <div className="text-muted" style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}>
                      Se activará el mapa de sala al abrir los lotes de cada sesión.
                    </div>
                  )}
                </MDBCol>

                {/* Descripción */}
                <MDBCol size="12">
                  <div className="form-outline">
                    <textarea
                      className="form-control"
                      rows={3}
                      value={description}
                      onChange={this.handleChange("description")}
                      disabled={saving}
                      id="funcion-desc-textarea"
                    />
                    <label className="form-label" htmlFor="funcion-desc-textarea">
                      Descripción
                    </label>
                  </div>
                </MDBCol>
              </MDBRow>
            </MDBModalBody>

            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={this.handleClose} disabled={saving}>
                Cancelar
              </MDBBtn>
              <MDBBtn color="primary" onClick={this.handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <MDBIcon fas icon="save" className="me-1" />
                    {isEdit ? "Guardar cambios" : "Crear función"}
                  </>
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default FuncionFormModal;
